import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export type AssignableDiscountCode = {
    id: string;
    name: string;
    description: string;
    type: string;
    discountValue: { toString(): string };
    startDate: string | Date;
    endDate: string | Date;
};

type DiscountCodeAssignmentListProps = {
    // undefined while loading
    discountCodes: AssignableDiscountCode[] | undefined;
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    hasChanges: boolean;
};

const DiscountCodeAssignmentList = ({ discountCodes, selectedIds, onChange, hasChanges }: DiscountCodeAssignmentListProps) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');

    const codes = discountCodes ?? [];
    const allSelected = codes.length > 0 && codes.every((code) => selectedIds.includes(code.id));

    const handleToggle = (discountCodeId: string) => {
        onChange(selectedIds.includes(discountCodeId) ? selectedIds.filter((id) => id !== discountCodeId) : [...selectedIds, discountCodeId]);
    };

    const handleToggleAll = () => {
        onChange(allSelected ? [] : codes.map((code) => code.id));
    };

    const query = search.trim().toLowerCase();
    const filteredCodes = codes.filter((code) => !query
        || code.name.toLowerCase().includes(query)
        || code.description.toLowerCase().includes(query));

    return (
        <>
            {codes.length > 0 && (
                <div className='max-w-xl space-y-3 mb-3'>
                    <div className='relative'>
                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                        <Input
                            placeholder='Rabattcodes filtern...'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className='pl-9 bg-white'
                        />
                    </div>
                    <label className='flex items-center gap-2 w-fit cursor-pointer'>
                        <Checkbox
                            checked={allSelected}
                            onCheckedChange={handleToggleAll}
                        />
                        <span className='text-sm'>Alle auswählen</span>
                    </label>
                </div>
            )}

            <div className='max-h-80 max-w-xl overflow-y-auto overflow-x-hidden border rounded-lg bg-white divide-y'>
                {discountCodes === undefined && (
                    <div className='p-3 space-y-2'>
                        <Skeleton className='h-8 w-full' />
                        <Skeleton className='h-8 w-full' />
                    </div>
                )}
                {filteredCodes.map((code) => {
                    const isSelected = selectedIds.includes(code.id);
                    const now = new Date();
                    const isExpired = now > new Date(code.endDate);
                    const isUpcoming = now < new Date(code.startDate);
                    return (
                        <label
                            key={code.id}
                            className='flex items-center gap-3 px-3 py-2 hover:bg-muted cursor-pointer transition-colors min-w-0'
                        >
                            <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleToggle(code.id)}
                            />
                            <div className='flex-1 min-w-0 overflow-hidden'>
                                <div className='flex items-center gap-2 min-w-0'>
                                    <span className='text-sm font-medium truncate min-w-0' title={code.name}>{code.name}</span>
                                    <Badge variant='outline' className='text-[10px] px-1.5 py-0 h-5 shrink-0'>
                                        {code.type === 'PERCENTAGE_DISCOUNT'
                                            ? `${code.discountValue}%`
                                            : `${Number(code.discountValue).toFixed(2)} €`}
                                    </Badge>
                                    {isExpired && (
                                        <Badge variant='outline' className='text-[10px] px-1.5 py-0 h-5 shrink-0 text-gray-500 border-gray-300'>
                                            Abgelaufen
                                        </Badge>
                                    )}
                                    {isUpcoming && (
                                        <Badge variant='outline' className='text-[10px] px-1.5 py-0 h-5 shrink-0 text-blue-600 border-blue-300'>
                                            Geplant
                                        </Badge>
                                    )}
                                </div>
                                {code.description && (
                                    <p className='text-xs text-muted-foreground truncate' title={code.description}>{code.description}</p>
                                )}
                            </div>
                            <Button
                                type='button'
                                variant='ghost'
                                size='icon'
                                className='h-7 w-7 shrink-0'
                                title='Rabattcode öffnen'
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/cms/discount-codes/${code.id}`);
                                }}
                            >
                                <ExternalLink className='h-3.5 w-3.5' />
                            </Button>
                        </label>
                    );
                })}
                {discountCodes !== undefined && codes.length === 0 && (
                    <p className='text-center text-sm text-muted-foreground py-4'>
                        Keine Rabattcodes mit Anwendungsebene &quot;Pro Einzelposition&quot; vorhanden.
                    </p>
                )}
            </div>
            {hasChanges && (
                <p className='text-sm text-amber-600 mt-2 flex items-center gap-1'>
                    <span>⚠</span> Ungespeicherte Änderungen
                </p>
            )}
        </>
    );
};

export default DiscountCodeAssignmentList;
